/**
 * Home Feature - Simplified Chinese (zh-CN)
 */

import type { HomeLocale } from './types';

const zhCN: HomeLocale = {
    home: {
        title: '用 AI 加速您的专利撰写',
        subtitle: '从交底书到审查意见答复，智能完成专利全流程文书',

        tabs: {
            patent: '专利申请',
            disclosure: '交底书',
            oar: '审查意见答复',
        },

        cardTitles: {
            patent: '专利申请',
            disclosure: '交底书',
            oar: '审查意见答复',
        },

        placeholders: {
            patent: '请描述您的技术方案、解决的问题以及创新之处。\n\n您也可以输入 {\'@\'} 使用模板或上传文件。',
            disclosure: '请描述您的发明构思、解决的技术问题以及关键创新点。\n\n您也可以输入 {\'@\'} 使用模板或上传文件。',
            oar: '请在此粘贴审查意见通知书内容，或描述您需要答复的驳回理由。',
        },

        placeholder: {
            default: '描述您的发明或输入 {\'@\'} 使用模板',
            template: '输入 {\'@\'} 浏览专利模板',
            example1: '例如：一种用于图像识别的机器学习系统...',
            example2: '例如：一种高容量改进型电池设计...',
            example3: '例如：{\'@\'} USPTO 软件专利模板',
        },

        workMode: {
            agent: '智能体',
            copilot: '助手',
        },

        actions: {
            addFiles: '添加文件',
            addFilesTooltip: '上传文件（PDF、Word、TXT）',
            dropToAdd: '松开以添加文件',
            style: '风格',
            styleTooltip: '选择撰写风格',
            template: '模板',
            templateTooltip: '选择专利模板',
            voiceInput: '语音输入',
            voiceInputTooltip: '语音输入',
            voiceInputSoon: '语音输入功能即将推出...',
            generate: '开始',
            loggingIn: '登录中...',
            startDrafting: '开始撰写',
            draftingStyle: '撰写风格',
            selectTemplate: '选择模板',
        },

        quickStart: {
            title: '快速开始常用撰写任务：',
            disclosure: '撰写交底书',
            specification: '撰写说明书',
            claims: '撰写权利要求',
            oar: '撰写答复',
            figures: '设计附图',
        },

        messages: {
            filesAdded: '已添加 {count} 个文件',
            pleaseEnterContent: '请输入内容或上传文件',
            pleaseUploadFile: '请上传审查意见文件',
            generating: '正在生成专利文档...',
            navigationFailed: '导航失败',
            styleApplied: '已应用风格：{name}',
            templateApplied: '已选择模板：{name}',
            mentionApplied: '已选择{type}：{name}',
            templateLoading: '正在加载模板：{name}',
            submitFailed: '提交失败，请重试',
            invalidFile: '无效的文件',
            uploadFailed: '文件上传失败，请重试',
            filesUploading: '文件正在上传中，请稍候',
            fileStatusUploading: '上传中...',
            fileStatusFailed: '上传失败',
            someFilesNotUploaded: '部分文件上传失败，请重新上传',
            emptyInput: '请输入内容或上传文件',
            emptyOarInput: '请输入专利申请号或上传文件',
            fileProcessed: '文件 {name} 解析完成',
            fileProcessFailed: '文件 {name} 解析失败',
            fileProcessingTimeout: '文件解析超时,请重新上传',
            filesProcessing: '文件正在解析中,请稍候...',
            filesNotReady: '部分文件尚未解析完成,请等待后再创建任务',
            fileAlreadyExists: '文件 {name} 已存在，直接使用',
            defaultPromptForPatent: '请解析上传的文档，生成技术交底书分析报告和专利申请文档。',
            defaultPromptForDisclosure: '按照技术交底书分析的标准流程处理文件，生成技术交底书',
        },

        // 统一的第一句话配置（根据任务类型和场景自动选择）
        initialPrompts: {
            patent_draft: {
                withFiles: '请解析上传的文档，生成技术交底书分析报告和专利申请文档。',
                empty: '请开始撰写专利申请',
            },
            disclosure: {
                withFiles: '按照技术交底书分析的标准流程处理文件，生成技术交底书',
                empty: '请开始撰写技术交底书',
            },
            prosecution: {
                withPatentNumber: '开始分析审查意见（申请号：{patentNumber}）',
                withFiles: '开始分析审查意见',
                empty: '请分析审查意见并生成答复文件',
            },
        },

        mention: {
            title: '模板',
            selectType: '选择类型',
            styleTitle: '风格',
            priorArt: '现有技术',
            file: '文件',
            document: '文档',
            loading: '加载中...',
            navigate: '导航',
            select: '选择',
            close: '关闭',
            noResults: '未找到 "{query}" 相关结果',
            tryDifferent: '尝试其他搜索词',
            docTypes: {
                patent_draft: '专利撰写',
                disclosure: '技术交底书',
                prosecution: '答复审查意见',
            },
            docStatus: {
                draft: '草稿',
                in_review: '审核中',
                approved: '已批准',
                filed: '已提交',
            },
            types: {
                template: {
                    label: '模板',
                    desc: '专利申请模板',
                },
                style: {
                    label: '撰写风格',
                    desc: '撰写风格设置',
                },
                priorArt: {
                    label: '现有技术',
                    desc: '现有技术专利',
                },
                file: {
                    label: '文件',
                    desc: '附件文件',
                },
                document: {
                    label: '文档',
                    desc: '项目文档',
                },
            },
            templates: {
                usptoUtilityPatent: 'USPTO 实用专利',
                usptoSoftwarePatent: 'USPTO 软件专利',
                epoPatentApplication: 'EPO 专利申请',
                pctInternationalApplication: 'PCT 国际申请',
                cnipaPatentApplication: 'CNIPA 专利申请',
            },
        },

        tooltips: {
            uploadFile: '上传文件',
            voiceInput: '语音输入',
        },

        patent: {
            jurisdiction: '请选择申请地区',
        },

        oar: {
            title: '审查意见答复',
            projectTitle: '审查意见答复 - {patentNumber}',
            projectTitleWithoutNumber: '审查意见答复',
            initialPrompt: '开始分析审查意见',
            initialPromptWithNumber: '开始分析审查意见（申请号：{patentNumber}）',
            confirmMessage: '已确认OAR分析结果，请继续处理',
            oarExtractList: 'OAR 提取列表',
            rejectionAnalysis: {
                title: '驳回分析',
                status: {
                    extracting: '提取数据中',
                    processing: '处理中',
                },
                button: {
                    confirm: '确认并继续',
                    saving: '保存中',
                    addClause: '添加条款',
                    showMore: '展开全部',
                    showLess: '收起',
                    export: '导出 Excel',
                    viewDetail: '查看逻辑链',
                },
                detail: {
                    drawerTitle: '分析详情',
                    examinerOpinion: '审查员意见',
                    featureComparison: '特征对比',
                    claimFeature: '本申请特征',
                    priorArtFeature: '对比文件特征',
                    priorArtLabel: '对比文件{no} · {name}',
                    noPriorArt: '无对比文件',
                    strategy: '答复策略',
                    strategyReason: '策略依据',
                    prevItem: '上一条',
                    nextItem: '下一条',
                },
                direction: {
                    agree: '同意',
                    unsure: '不确定',
                    disagree: '不同意',
                },
                basicInfo: {
                    application: '申请号',
                    oaNo: '审查意见编号',
                    type: '类型',
                },
                tabs: {
                    all: '全部',
                },
                tableHeaders: {
                    claim: '权利要求',
                    statute: '法条',
                    claimAndStatute: '权利要求/法条',
                    claimElement: '权利要求要素',
                    examinerReasoning: '审查员理由',
                    aiAnalysis: 'AI分析',
                    strategy: '答复策略',
                },
                statusPills: {
                    agree: '认可',
                    unsure: '不确定',
                    disagree: '不认可',
                },
                labels: {
                    direction: '答复方向',
                    strategy: '答复策略',
                },
                tooltips: {
                    direction: '是否认同审查员观点',
                    strategy: '针对该驳回理由的答复策略',
                },
                placeholder: {
                    edit: '点击编辑...',
                },
                emptyState: {
                    noData: '暂无驳回数据',
                    hint: '提取完成后将在此显示驳回分析',
                },
                message: {
                    noDataId: '无法获取数据ID',
                    noData: '暂无数据',
                    saveSuccess: '保存成功',
                    saveFailed: '保存失败',
                    deleteSuccess: '删除成功',
                },
            },
            region: {
                uspto: 'USPTO',
                epo: 'EPO',
                cnipa: 'CNIPA',
            },
            patentNumber: {
                label: '专利申请号',
                placeholder: '输入专利/申请号（例如：US16/892,134）以加载审查历史。或上传 File Wrapper 文件。',
            },
            uploadAction: {
                title: '上传审查意见',
                choose: '选择文件',
                description: '请选择审查意见文件（PDF）及相关专利申请文件',
                uploading: '正在上传',
                addMore: '继续上传更多文件',
            },
            uploadedFiles: {
                title: '已上传文件 ({count})',
            },
            fileStatus: {
                preparing: '准备文件...',
                analyzing: '分析内容...',
                finalizing: '即将完成...',
                processing: '处理中...',
                estimatedTime: '预计需要 1-2 分钟',
                completed: '已完成',
                failed: '解析失败',
            },
            actions: {
                agent: 'Agent',
                copilot: 'Copilot',
                start: '开始',
            },
            search: {
                failed: '专利搜索失败，请重试',
                notFound: '未找到专利',
            },
            patent: {
                publicationNumber: '公开(公告)号：',
                publicationDate: '公开公告日：',
                currentApplicant: '[标]当前申请(专利权)人：',
            },
            download: {
                searching: '正在搜索审查意见PDF...',
                downloading: 'PDF下载已开始，请检查浏览器下载',
                success: 'PDF下载成功',
                failed: 'PDF下载失败，请重试',
                notFound: '未找到审查意见PDF，您可以手动上传',
                visitUsptoWebsite: '访问 USPTO 网站下载',
            },
            unsupportedRegion: '暂不支持该地区',
        },

        draftingStyle: {
            title: '选择撰写风格',
            stylesHeader: '风格列表',
            previewHeader: '预览',
            cancel: '取消',
            apply: '应用风格',
            loading: '加载中...',
            loadingPreview: '加载预览中...',
            loadError: '加载风格列表失败',
            loadDetailError: '加载风格详情失败',
            noStyles: '暂无可用风格',
            noPreview: '暂无预览内容',
            pleaseSelect: '请先选择风格',
            used: '已使用 {count} 次',
            styles: {
                standard: {
                    name: '标准格式',
                    description: '传统专利格式，带编号段落和正式语言',
                },
                detailed: {
                    name: '详细技术',
                    description: '全面的技术描述，包含丰富的实施例细节',
                },
                concise: {
                    name: '简洁权利要求导向',
                    description: '精简格式，强调清晰有力的权利要求语言',
                },
                international: {
                    name: 'PCT 国际申请',
                    description: '针对 PCT 国际专利申请优化的格式',
                },
                continuation: {
                    name: '延续申请',
                    description: '引用母案申请的延续或分案申请格式',
                },
            },
        },

        templateSelection: {
            title: '选择模板',
            templatesHeader: '模板列表',
            previewHeader: '预览',
            cancel: '取消',
            apply: '应用模板',
            loading: '加载中...',
            loadingPreview: '加载预览中...',
            loadError: '加载模板列表失败',
            loadDetailError: '加载模板详情失败',
            noTemplates: '暂无可用模板',
            noPreview: '暂无预览内容',
            pleaseSelect: '请先选择模板',
        },

        documentModifications: {
            title: '文档修改记录',
            summary: '共 {count} 条修改',
            emptyState: '暂无修改记录',
            tableHeaders: {
                index: '#',
                documentId: '文档 ID',
                range: '位置范围',
                length: '修改字符数',
                content: '修改内容',
            },
            exportExcel: '导出 Excel',
        },
    },
};

export default zhCN;
